import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { KINETIC_WORD_SETTING } from './kineticWordSetting';
import { check, someTween } from '../skillUsage';

export const figureGroundSplitSkill: CodeoSkill = {
    name: SKILL_NAMES.figureGroundSplit,
    kind: 'codeo',
    description:
        'Split the frame into two opposite colour fields with the type running across the seam, ' +
        "each part of the word drawn in the other field's colour, so the word is literally " +
        'negative on one side — for opposites, "versus", before and after, either/or, two sides.',
    checkLooksFor:
        'two stacked copies of the frame in inverted colours, the top one cut by a clip-path inset or polygon that is tweened',
    documentUsesSkill: check(
        (script, markup) =>
            someTween(script, /clip-?[Pp]ath/, /inset\(|polygon\(/) && /aria-hidden/.test(markup),
    ),
    reviewNote:
        'A word drawn half in one colour and half in its opposite, with the background inverting at ' +
        'the same seam, is this technique working: it is one word on two grounds, not two words.',
    instructions: `# Figure–Ground Split

Two opposites in one frame. The frame is **two fields of opposite colour** — dark and light, or the accent and its complement — meeting at a seam. The type runs across the seam, and **each part of it is drawn in the other field's colour**: light on the dark side, dark on the light side. The word stays whole and readable while it is visibly negative on one side. The seam moves on the beat: it wipes across the frame, flipping the type as it passes, and stops where it splits the idea.

Use it where the copy holds two opposed things: opposites, versus, before and after, either/or, light and dark, old and new. Two opposed words stack, one per line, and the seam runs between them or through both.

${KINETIC_WORD_SETTING}

## Markup

The frame is built twice, identically, in opposite colours: the base layer, and on top of it an inverted copy that is cut by a \`clip-path\`. Moving the clip moves the seam. The top copy is \`aria-hidden\`.

\`\`\`html
<div id="fg" class="absolute inset-0">
  <div class="fg-layer fg-dark absolute inset-0 flex flex-col items-center justify-center">
    <div class="fg-word" data-l5-allow-literal>LIGHT</div>
    <div class="fg-word" data-l5-allow-literal>DARK</div>
  </div>
  <div id="fg-invert" class="fg-layer fg-light absolute inset-0 flex flex-col items-center justify-center" aria-hidden="true">
    <div class="fg-word" data-l5-allow-literal>LIGHT</div>
    <div class="fg-word" data-l5-allow-literal>DARK</div>
  </div>
</div>
\`\`\`

\`\`\`css
.fg-word { line-height: 1; white-space: nowrap; font-family: var(--l5-font-0), sans-serif; font-weight: 800; text-transform: uppercase; }
.fg-dark { background: var(--l5-color-0, #111); color: var(--l5-color-3, #f2f2f2); }
.fg-light { background: var(--l5-color-3, #f2f2f2); color: var(--l5-color-0, #111); }
\`\`\`

## Timeline code

The two layers must place the type identically, so fit both from the same measurement.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const words = [...document.querySelectorAll('.fg-dark .fg-word')];
const copies = [...document.querySelectorAll('.fg-light .fg-word')];
words.forEach((word, i) => fitWord(word, 0.8, (size) => { copies[i].style.fontSize = size; }));

const CUE_IN = 0.3, CUE_SPLIT = 0.75;
// The inverted copy starts cut away entirely, then its edge sweeps in from the right...
tl.fromTo('#fg-invert',
  { clipPath: 'inset(0% 0% 0% 100%)' },
  { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.35, ease: 'power3.inOut' }, CUE_IN);
// ...and pulls back to stop dead on the middle, leaving the frame split down the seam.
tl.to('#fg-invert', { clipPath: 'inset(0% 0% 0% 50%)', duration: 0.3, ease: 'power3.out' }, CUE_SPLIT);
tl.set({}, {}, 2.0);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Horizontal seam.** For two stacked opposites, cut on the vertical: \`inset(50% 0% 0% 0%)\` puts the second word on the other ground.
- **A diagonal seam.** \`clip-path: polygon(...)\` with four points, tweened the same way, for a sharper, more graphic split.
- **Flip on the beat.** For "turns into its opposite", sweep the seam all the way across on the word, so the whole frame inverts.
- **Arrive first.** The type can rise in with \`${SKILL_NAMES.letterMaskRise}\` on both layers at once (the same tweens on both copies) before the seam moves.

## Pitfalls

- **Identical layers.** Any difference in size or position between the two copies shows as a jagged, doubled word at the seam. Build them from one measurement, with the same markup.
- **Opposite colours only.** The two fields have to be each other's negative, or the word reads as two colours rather than as inverted.
- **One seam.** A second split is noise; the idea is two sides.
- The clip lives on the top layer only; the base layer is never clipped.`,
};
